import { z } from "zod"

export const DocumentType = {
  PurchaseOrder: "PurchaseOrder",
  FashionDemandPlan: "FashionDemandPlan",
  // Comments here live on the SupplyPlan (parent of N PurchasePlans/"buys"), scoped to
  // the supply plan's collection ids so they persist across new buys within the same plan.
  FashionSupplyPlan: "FashionSupplyPlan",
  // Variant-level notes on a buy plan (PurchasePlan). documentId = purchasePlanId, rowId = variantId.
  BuyPlan: "BuyPlan",
  // Variant-level notes in the Demand 2.0 workspace, scoped to the collection ids being planned so
  // they survive republishing and group/member scope changes. documentId = collectionId,
  // rowId = variantId — the same key space as FashionDemandPlan, so the older plan's comments can be
  // surfaced alongside during the transition.
  Demand2Plan: "Demand2Plan",
} as const

export type DocumentType = (typeof DocumentType)[keyof typeof DocumentType]

export const DocumentTypeSchema = z.enum([
  DocumentType.PurchaseOrder,
  DocumentType.FashionDemandPlan,
  DocumentType.FashionSupplyPlan,
  DocumentType.BuyPlan,
  DocumentType.Demand2Plan,
])

// Human-readable label for a document type, e.g. shown in the comment card to indicate
// which document the comment was originally made on.
export const DocumentTypeLabel: Record<DocumentType, string> = {
  PurchaseOrder: "Purchase Order",
  FashionDemandPlan: "Fashion Demand Plan",
  FashionSupplyPlan: "Fashion Supply Plan",
  BuyPlan: "Buy",
  Demand2Plan: "Demand Plan",
}

// A single addressable comment-source: a documentType paired with the documentIds that
// identify the document under that type. For PurchaseOrder this is `[poId]`; for plan
// types it's the plan's collection ids. Multiple sources can be queried in one request
// to surface comments from cross-document contexts (e.g., Supply Plan view also shows
// Demand Plan comments for the same collections).
export type CommentSource = {
  documentType: DocumentType
  documentIds: string[]
}

export const CommentSourceSchema = z.object({
  documentType: DocumentTypeSchema,
  documentIds: z.array(z.string()),
})
